/**
 * AuditLogPage — unified feed of who did what, when, across every vendor.
 *
 * Displays events from django-easy-audit's three tables (CRUDEvent /
 * LoginEvent / RequestEvent) as a single timeline with multi-facet filters
 * (kind + vendor + free-text). Rows expand to show the full structured
 * detail captured by the backend.
 *
 * The "request" event kind is hidden by default since easy-audit logs every
 * HTTP request and the noise drowns out the interesting events.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowDownToLine,
  ChevronDown,
  ChevronRight,
  LogIn,
  LogOut,
  Plus,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  Star,
  Trash2,
  Pencil,
  Globe,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { PanelSkeleton } from "@/components/ui/skeleton";
import { useAuditLog, type AuditFilters } from "@/stores/audit";
import type { AuditEvent, AuditEventKind } from "@/api/schemas/audit";
import {
  loadSavedAuditSearches,
  addSavedAuditSearch,
  removeSavedAuditSearch,
  type SavedAuditSearch,
} from "@/lib/audit-saved-searches";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

/* -------- helpers -------- */

/** Format an ISO timestamp as compact relative time. */
function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

/** Visual treatment per event kind. */
function kindStyle(k: AuditEventKind): {
  icon: React.ReactNode;
  pill: "ok" | "info" | "warn" | "err" | "neutral";
  label: string;
} {
  switch (k) {
    case "create":
      return { icon: <Plus size={12} />, pill: "ok", label: "create" };
    case "update":
      return { icon: <Pencil size={12} />, pill: "info", label: "update" };
    case "delete":
      return { icon: <Trash2 size={12} />, pill: "err", label: "delete" };
    case "login":
      return { icon: <LogIn size={12} />, pill: "ok", label: "login" };
    case "logout":
      return { icon: <LogOut size={12} />, pill: "neutral", label: "logout" };
    case "login_failed":
      return {
        icon: <ShieldAlert size={12} />,
        pill: "err",
        label: "login failed",
      };
    case "request":
      return { icon: <Globe size={12} />, pill: "neutral", label: "request" };
  }
}

/** All event kinds — used as a master list for the filter chip group. */
const ALL_KINDS: AuditEventKind[] = [
  "create",
  "update",
  "delete",
  "login",
  "logout",
  "login_failed",
  "request",
];
const ALL_VENDORS: Array<"meraki" | "aruba" | "shasta" | "system"> = [
  "meraki",
  "aruba",
  "shasta",
  "system",
];

/** Default-hidden kinds (request is too noisy to show by default). */
const NOISY_KINDS: AuditEventKind[] = ["request"];

/* ============================================ */
/* PAGE                                         */
/* ============================================ */

export function AuditLogPage() {
  const animatedRootRef = useUiAutoAnimate();

  const {
    data,
    total,
    filters,
    status,
    error,
    load,
    setFilters,
    resetFilters,
  } = useAuditLog();

  // Initial load — also sets a default "hide request" filter so the page
  // opens with the interesting events first.
  useEffect(() => {
    setFilters({ kinds: ALL_KINDS.filter((k) => !NOISY_KINDS.includes(k)) });
  }, [setFilters]);

  // Local query state — debounced into the store so we don't refetch on
  // every keystroke. Since the store falls back to fixture filtering, the
  // refetch is essentially free in this Phase but the pattern is correct.
  // Seeded from the `?model=` URL param so the sidebar's "Agent Activity"
  // deep-link (/audit?model=AIAgent) lands pre-filtered.
  const [searchParams] = useSearchParams();
  const [localQuery, setLocalQuery] = useState(() => searchParams.get("model") ?? "");
  useEffect(() => {
    const t = setTimeout(() => setFilters({ query: localQuery }), 250);
    return () => clearTimeout(t);
  }, [localQuery, setFilters]);

  const [savedSearches, setSavedSearches] = useState<SavedAuditSearch[]>(() => loadSavedAuditSearches());

  // Group events by day so the timeline reads as a chronological narrative.
  const groupedByDay = useMemo(() => {
    const map = new Map<string, AuditEvent[]>();
    for (const e of data) {
      const day = new Date(e.occurredAt).toDateString();
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(e);
    }
    return Array.from(map.entries());
  }, [data]);

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Audit log</span>
      </div>

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Audit log
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Unified feed of every change made through Opus — config pushes, user
            actions, sign-ins, and failed access attempts. Backed by
            django-easy-audit; nothing here is editable.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/audit/archives">
              <ArrowDownToLine size={13} />
              Archives
            </Link>
          </Button>
          <Button
            size="sm"
            onClick={() => void load()}
            disabled={status === "loading"}
          >
            <RefreshCw size={13} />
            Refresh
          </Button>
        </div>
      </div>

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      <AuditFilterBar
        filters={filters}
        setFilters={setFilters}
        resetFilters={resetFilters}
        total={total}
        localQuery={localQuery}
        setLocalQuery={setLocalQuery}
        savedSearches={savedSearches}
        setSavedSearches={setSavedSearches}
      />

      {/* Timeline grouped by day */}
      {status === "loading" && data.length === 0 ? (
        <div className="flex flex-col gap-3">
          <PanelSkeleton />
          <PanelSkeleton />
          <PanelSkeleton />
        </div>
      ) : data.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <ShieldCheck size={20} className="text-violet" />
            <div className="text-sm font-semibold text-fg">
              No events match your filters.
            </div>
            <div className="text-xs text-fg-mute">
              Try widening the filter or clearing it.{" "}
              {/* "request" events are hidden by default; new users often
                  only have those (every page-load is one). Hint at the
                  toggle so they don't think the page is broken. */}
              <span className="text-fg-dim">
                Tip: HTTP{" "}
                <code className="rounded bg-surface-2 px-1 py-0.5 font-mono">
                  request
                </code>{" "}
                events are hidden by default — add that kind chip to see them.
              </span>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div data-testid="audit-timeline" className="flex flex-col gap-5">
          {groupedByDay.map(([day, events]) => (
            <div key={day} className="flex flex-col gap-2">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.12em] text-fg-mute">
                <span>{day}</span>
                <span className="h-px flex-1 bg-line" />
                <span>
                  {events.length} event{events.length === 1 ? "" : "s"}
                </span>
              </div>
              <Card>
                <ul className="m-0 flex list-none flex-col p-0">
                  {events.map((e) => (
                    <EventRow key={e.id} event={e} />
                  ))}
                </ul>
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================ */
/* FILTER BAR                                   */
/* ============================================ */

function AuditFilterBar({
  filters,
  setFilters,
  resetFilters,
  total,
  localQuery,
  setLocalQuery,
  savedSearches,
  setSavedSearches,
}: {
  filters: AuditFilters;
  setFilters: (patch: Partial<AuditFilters>) => void;
  resetFilters: () => void;
  total: number;
  localQuery: string;
  setLocalQuery: (v: string) => void;
  savedSearches: SavedAuditSearch[];
  setSavedSearches: (s: SavedAuditSearch[]) => void;
}) {
  function applySaved(s: SavedAuditSearch) {
    setLocalQuery(s.filters.query);
    setFilters({
      kinds: s.filters.kinds,
      vendors: s.filters.vendors,
      query: s.filters.query,
    });
  }
  function saveCurrent() {
    const name = window.prompt(
      "Name this search (e.g. 'Failed logins this week')",
    );
    if (!name) return;
    addSavedAuditSearch(name, { ...filters, query: localQuery });
    setSavedSearches(loadSavedAuditSearches());
  }
  function removeSaved(id: string) {
    setSavedSearches(removeSavedAuditSearch(id));
  }
  function toggleKind(k: AuditEventKind) {
    const has = filters.kinds.includes(k);
    setFilters({
      kinds: has ? filters.kinds.filter((x) => x !== k) : [...filters.kinds, k],
    });
  }
  function toggleVendor(v: (typeof ALL_VENDORS)[number]) {
    const has = filters.vendors.includes(v);
    setFilters({
      vendors: has
        ? filters.vendors.filter((x) => x !== v)
        : [...filters.vendors, v],
    });
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search
              size={14}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
            />
            <Input
              value={localQuery}
              onChange={(e) => setLocalQuery(e.target.value)}
              placeholder="Filter by user, summary, model, IP, record id…"
              className="pl-9"
            />
          </div>
          <span className="ml-auto font-mono text-xs text-fg-mute">
            {total} events
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={saveCurrent}
            title="Save the current filter set as a named search"
          >
            <Star size={13} />
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setLocalQuery("");
              resetFilters();
            }}
          >
            Reset
          </Button>
        </div>

        {savedSearches.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
              <Star size={10} className="mr-1 inline" />
              Saved
            </span>
            {savedSearches.map((s) => (
              <span
                key={s.id}
                className="inline-flex items-center gap-1 rounded-full border border-violet/40 bg-aurora-soft px-2 py-0.5 text-[11px] font-semibold text-violet"
              >
                <button
                  type="button"
                  onClick={() => applySaved(s)}
                  title={`Apply '${s.name}' (saved ${timeAgo(s.createdAt)})`}
                  className="hover:underline"
                >
                  {s.name}
                </button>
                <button
                  type="button"
                  onClick={() => removeSaved(s.id)}
                  title="Remove"
                  className="rounded-full text-violet/70 hover:text-violet"
                  aria-label={`Remove saved search ${s.name}`}
                >
                  <X size={9} />
                </button>
              </span>
            ))}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
            Kind
          </span>
          {ALL_KINDS.map((k) => {
            const active = filters.kinds.includes(k);
            const sty = kindStyle(k);
            return (
              <button
                key={k}
                type="button"
                onClick={() => toggleKind(k)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors",
                  active
                    ? "border-violet/40 bg-aurora-soft text-violet"
                    : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
                )}
              >
                {sty.icon}
                {sty.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
            Vendor
          </span>
          {ALL_VENDORS.map((v) => {
            const active = filters.vendors.includes(v);
            return (
              <button
                key={v}
                type="button"
                onClick={() => toggleVendor(v)}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-[11px] font-semibold capitalize transition-colors",
                  active
                    ? "border-violet/40 bg-aurora-soft text-violet"
                    : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
                )}
              >
                {v}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/* ============================================ */
/* SINGLE EVENT ROW                             */
/* ============================================ */

function EventRow({ event }: { event: AuditEvent }) {
  const [open, setOpen] = useState(false);
  const sty = kindStyle(event.kind);
  const hasDetail = !!event.detail || !!event.modelName;

  return (
    <li className="border-t border-line/60 first:border-t-0">
      <button
        type="button"
        onClick={() => hasDetail && setOpen((v) => !v)}
        disabled={!hasDetail}
        className={cn(
          "grid w-full grid-cols-[24px_28px_1fr_auto_auto_72px] items-center gap-3 px-5 py-3 text-left transition-colors",
          hasDetail ? "cursor-pointer hover:bg-surface-2/40" : "cursor-default",
        )}
      >
        {hasDetail ? (
          open ? (
            <ChevronDown size={14} className="text-fg-mute" />
          ) : (
            <ChevronRight size={14} className="text-fg-mute" />
          )
        ) : (
          <span />
        )}
        <span
          className={cn(
            "inline-flex h-6 w-6 items-center justify-center rounded-full",
            sty.pill === "ok" && "bg-mint/15 text-mint",
            sty.pill === "info" && "bg-violet/15 text-violet",
            sty.pill === "warn" && "bg-peach/15 text-peach",
            sty.pill === "err" && "bg-rose/15 text-rose",
            sty.pill === "neutral" && "bg-line/30 text-fg-mute",
          )}
        >
          {sty.icon}
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm text-fg">{event.summary}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[10.5px] text-fg-mute">
            <span className="font-mono">{event.user ?? "anonymous"}</span>
            {event.remoteIp ? (
              <>
                <span>·</span>
                <span className="font-mono">{event.remoteIp}</span>
              </>
            ) : null}
            {event.modelName ? (
              <>
                <span>·</span>
                <span className="font-mono">{event.modelName}</span>
              </>
            ) : null}
          </div>
        </div>
        {event.vendor ? (
          <Badge variant="neutral" size="sm">
            {event.vendor}
          </Badge>
        ) : (
          <span />
        )}
        <Badge variant={sty.pill} size="sm">
          {sty.label}
        </Badge>
        <span className="text-right font-mono text-[10.5px] text-fg-mute">
          {timeAgo(event.occurredAt)}
        </span>
      </button>

      {/* Expanded detail */}
      {open && hasDetail ? (
        <div className="border-t border-line/60 bg-surface-tint/40 px-5 py-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 font-mono text-[11px]">
            <div className="text-fg-mute">timestamp</div>
            <div className="text-fg">{event.occurredAt}</div>
            {event.modelName ? (
              <>
                <div className="text-fg-mute">model</div>
                <div className="text-fg">{event.modelName}</div>
              </>
            ) : null}
            {event.recordId ? (
              <>
                <div className="text-fg-mute">record_id</div>
                <div className="text-fg">{event.recordId}</div>
              </>
            ) : null}
            {event.detail ? (
              <>
                <div className="self-start text-fg-mute">detail</div>
                <pre className="m-0 overflow-x-auto rounded-lg border border-line bg-surface p-2 text-[11px] text-fg">
                  {JSON.stringify(event.detail, null, 2)}
                </pre>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </li>
  );
}
